import { Group, Stack, useMantineTheme } from '@mantine/core';
import type { BiMetricConfig, LineChartConfig } from '@repo/api-codegen';
import { Switch } from '@repo/foundations';
import { cloneDeep } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { SelectableProperty } from '../../../components/EntityPageLayout/EntityPropertySidebar';
import { ChartConfigHelper } from '../../../utils/charts/bi/ChartConfigHelper';

interface VisualSectionProps {
	chartConfig: BiMetricConfig['chart_config'];
	onUpdate: (config: BiMetricConfig['chart_config']) => void;
}

const updateShowLegend = (
	config: BiMetricConfig['chart_config'],
	value: boolean
): BiMetricConfig['chart_config'] => {
	const newConfig = cloneDeep(config);
	newConfig.showLegend = value;
	return newConfig;
};

const updateGridLines = (
	config: BiMetricConfig['chart_config'],
	value: boolean
): BiMetricConfig['chart_config'] => {
	const newConfig = cloneDeep(config);
	newConfig.showGrid = value;
	return newConfig;
};

const updateSmoothLine = (
	config: BiMetricConfig['chart_config'],
	value: boolean
): BiMetricConfig['chart_config'] => {
	const newConfig = cloneDeep(config);

	// Only apply to line charts
	if (config.chartType === 'line') {
		const lineConfig = newConfig as LineChartConfig;
		lineConfig.lineType = value ? 'smooth' : 'normal';
	}

	return newConfig;
};

export const VisualSection = observer(
	({ chartConfig, onUpdate }: VisualSectionProps) => {
		const mantineTheme = useMantineTheme();
		const chartConfigHelper = new ChartConfigHelper(chartConfig, mantineTheme);
		const { isLineChart, showLegend, showGrid, lineType } = chartConfigHelper;

		// Get current values with proper defaults
		const gridLines = showGrid;
		const smoothLine = lineType === 'smooth';

		return (
			<Stack spacing="xs">
				{/* TODO: Add back in after we have a way to edit legends */}
				{/* <SelectableProperty label="Show legends" dataTestId="show-legends">
					<Group w="100%" position="right">
						<Switch
							checked={showLegend}
							onChange={(event) => {
								onUpdate(
									updateShowLegend(chartConfig, event.currentTarget.checked)
								);
							}}
						/>
					</Group>
				</SelectableProperty> */}

				<SelectableProperty label="Show grid" dataTestId="show-grid">
					<Group w="100%" position="right">
						<Switch
							checked={gridLines}
							onChange={(event) => {
								onUpdate(
									updateGridLines(chartConfig, event.currentTarget.checked)
								);
							}}
						/>
					</Group>
				</SelectableProperty>

				{isLineChart && (
					<SelectableProperty label="Smooth line" dataTestId="smooth-line">
						<Group w="100%" position="right">
							<Switch
								checked={smoothLine}
								onChange={(event) => {
									onUpdate(
										updateSmoothLine(chartConfig, event.currentTarget.checked)
									);
								}}
							/>
						</Group>
					</SelectableProperty>
				)}
			</Stack>
		);
	}
);
